import type { VatMode } from './money';

/**
 * Module 1.8 (Prognooside haldus) — the ONE definition of how a recurring
 * cash-flow history becomes a forward projection (master rule: one truth per
 * number; L9: integer cents only, never a float).
 *
 * The operator's DECISIONS live in `pgfin.forecast_rule` (party or payroll,
 * method, horizon). The €-figures are NEVER stored on the rule — they are
 * derived here from history at every materialization, so a freshly synced
 * month immediately reshapes next months' forecast. The API materializer
 * turns these numbers into ordinary `cash_flow_entry` rows
 * (source 'forecast', certainty 'forecast', deterministic external_ids) and
 * NETS THE FORECAST DOWN against reality: a real invoice that lands in a
 * forecast month reduces that month's forecast row instead of double-counting
 * (see {@link netDownGrossCents}).
 *
 * AI is nowhere in this path — every number is arithmetic over history (L6).
 */

/** How a rule turns monthly history into next month's figure. */
export const FORECAST_METHODS = ['last', 'weighted_avg'] as const;
export type ForecastMethod = (typeof FORECAST_METHODS)[number];

/** UI labels stay Estonian (replit.md Terminology). */
export const FORECAST_METHOD_LABELS_ET: Record<ForecastMethod, string> = {
  last: 'viimane kuu',
  weighted_avg: 'kaalutud keskmine (3-2-1)',
};

/**
 * Below this many distinct history months a projection is statistically weak —
 * the approved canvas DISABLES activation for such a party ("Ebapiisav
 * ajalugu") and the API rejects it at the boundary; the flag here is the ONE
 * definition both read.
 */
export const FORECAST_MIN_HISTORY_MONTHS = 4;

/** The 3-2-1 weights of `weighted_avg`, newest month first. */
const WEIGHTED_AVG_WEIGHTS = [3, 2, 1] as const;

const MONTH_RE = /^\d{4}-(0[1-9]|1[0-2])$/;

function assertMonth(month: string, label: string): void {
  if (!MONTH_RE.test(month)) {
    throw new Error(`${label} must be YYYY-MM, got "${month}"`);
  }
}

function assertIntCents(cents: number, label: string): void {
  if (!Number.isInteger(cents)) {
    throw new Error(`${label} must be integer cents, got ${cents}`);
  }
}

/** One month's realized total for a series being projected. */
export interface MonthlyTotal {
  /** Calendar month, `YYYY-MM`. */
  month: string;
  /** Realized total of that month, integer cents (gross for party series). */
  cents: number;
}

/** The projection figures one monthly series supports. */
export interface SeriesForecast {
  /** Distinct history months with data. */
  monthsSeen: number;
  /** Newest history month (`YYYY-MM`), or null with no history. */
  lastMonth: string | null;
  /** The newest month's total, integer cents — the `last` method's figure. */
  lastCents: number;
  /**
   * Plain average over ALL seen months, integer cents — the "Kuu keskmine"
   * reference column of the canvas. Never a method's figure, context only.
   */
  avgCents: number;
  /**
   * 3-2-1 weighted average of the newest ≤3 months, integer cents — the
   * `weighted_avg` method's figure. With fewer than 3 months the weights
   * truncate (2 months → 3-2, 1 month → 3), so the figure is always an
   * average of what EXISTS, never diluted by imaginary zero months.
   */
  weightedAvgCents: number;
  /** True below {@link FORECAST_MIN_HISTORY_MONTHS} distinct months. */
  insufficientHistory: boolean;
}

/**
 * Project one monthly series (a party's gross totals, the payroll net fund,
 * the TSD figures — any series). Input order is free and the same month may
 * appear split (entries are summed); every cent must be an integer (L9).
 */
export function seriesForecastOf(totals: readonly MonthlyTotal[]): SeriesForecast {
  const byMonth = new Map<string, number>();
  for (const t of totals) {
    assertMonth(t.month, 'MonthlyTotal.month');
    assertIntCents(t.cents, 'MonthlyTotal.cents');
    byMonth.set(t.month, (byMonth.get(t.month) ?? 0) + t.cents);
  }
  const months = [...byMonth.keys()].sort();
  const monthsSeen = months.length;
  if (monthsSeen === 0) {
    return {
      monthsSeen: 0,
      lastMonth: null,
      lastCents: 0,
      avgCents: 0,
      weightedAvgCents: 0,
      insufficientHistory: true,
    };
  }
  let total = 0;
  for (const cents of byMonth.values()) {
    total += cents;
  }
  const newestFirst = [...months].reverse().slice(0, WEIGHTED_AVG_WEIGHTS.length);
  let weightedSum = 0;
  let weightTotal = 0;
  for (let i = 0; i < newestFirst.length; i += 1) {
    const weight = WEIGHTED_AVG_WEIGHTS[i];
    weightedSum += weight * (byMonth.get(newestFirst[i]) as number);
    weightTotal += weight;
  }
  const lastMonth = months[monthsSeen - 1];
  return {
    monthsSeen,
    lastMonth,
    lastCents: byMonth.get(lastMonth) as number,
    avgCents: Math.round(total / monthsSeen),
    weightedAvgCents: Math.round(weightedSum / weightTotal),
    insufficientHistory: monthsSeen < FORECAST_MIN_HISTORY_MONTHS,
  };
}

/** The method's projected figure — the ONE mapping from method to number. */
export function forecastAmountCents(
  forecast: Pick<SeriesForecast, 'lastCents' | 'weightedAvgCents'>,
  method: ForecastMethod,
): number {
  return method === 'last' ? forecast.lastCents : forecast.weightedAvgCents;
}

/** One history entry of a recurring party (a non-forecast cash-flow row). */
export interface PartyHistoryRow {
  /** Calendar month of the entry's expected date, `YYYY-MM`. */
  month: string;
  /** The entry's gross, integer cents (credit notes arrive negative). */
  grossCents: number;
  /** The entry's VAT mode — the forecast inherits the DOMINANT one. */
  vatMode: VatMode;
  /** Day-of-month of the entry's expected date, 1–31. */
  dueDay: number;
}

/**
 * Tie-break order when two VAT modes carry equal history weight — the
 * domestic default wins (L11: domestic 24% is the house policy).
 */
const VAT_MODE_PRIORITY: readonly VatMode[] = ['domestic', 'foreign', 'reduced', 'exempt'];

/** A party's projection: the series figures + the shape of a typical entry. */
export interface PartyForecast extends SeriesForecast {
  /**
   * Lower median of the history rows' due days (1–31) — the day the forecast
   * row lands on (clamped per month by {@link forecastDueDate}). Neutral
   * mid-month 15 with no history.
   */
  medianDueDay: number;
  /**
   * The VAT mode carrying the largest |gross| across history — the forecast
   * row's mode; its rate is resolved by the vatEngine policy (L11), never
   * hardcoded here. Domestic with no history.
   */
  dominantVatMode: VatMode;
}

/** Project one recurring party from its non-forecast history rows. */
export function partyForecastOf(rows: readonly PartyHistoryRow[]): PartyForecast {
  const modeWeights = new Map<VatMode, number>();
  const dueDays: number[] = [];
  for (const row of rows) {
    if (!Number.isInteger(row.dueDay) || row.dueDay < 1 || row.dueDay > 31) {
      throw new Error(`PartyHistoryRow.dueDay must be an integer 1–31, got ${row.dueDay}`);
    }
    assertIntCents(row.grossCents, 'PartyHistoryRow.grossCents');
    modeWeights.set(row.vatMode, (modeWeights.get(row.vatMode) ?? 0) + Math.abs(row.grossCents));
    dueDays.push(row.dueDay);
  }
  const series = seriesForecastOf(rows.map((r) => ({ month: r.month, cents: r.grossCents })));

  let dominantVatMode: VatMode = 'domestic';
  let dominantWeight = -1;
  for (const mode of VAT_MODE_PRIORITY) {
    const weight = modeWeights.get(mode) ?? -1;
    if (weight > dominantWeight) {
      dominantVatMode = mode;
      dominantWeight = weight;
    }
  }

  dueDays.sort((a, b) => a - b);
  const medianDueDay = dueDays.length === 0 ? 15 : dueDays[Math.floor((dueDays.length - 1) / 2)];

  return { ...series, medianDueDay, dominantVatMode };
}

/**
 * Materialization is bounded: a rule projects at most this many months ahead
 * (5 years), so a fat-fingered horizon can never explode the calendar.
 */
export const MAX_FORECAST_MONTHS = 60;

/** Every `YYYY-MM` from `fromMonth` through `toMonth`, both inclusive. */
export function forecastMonthsBetween(fromMonth: string, toMonth: string): string[] {
  assertMonth(fromMonth, 'fromMonth');
  assertMonth(toMonth, 'toMonth');
  const out: string[] = [];
  let [y, m] = fromMonth.split('-').map(Number);
  while (`${y}-${String(m).padStart(2, '0')}` <= toMonth) {
    out.push(`${y}-${String(m).padStart(2, '0')}`);
    if (out.length > MAX_FORECAST_MONTHS) {
      throw new Error(`forecast span exceeds ${MAX_FORECAST_MONTHS} months`);
    }
    m += 1;
    if (m === 13) {
      m = 1;
      y += 1;
    }
  }
  return out;
}

/** The due date of a forecast row in `month`: `dueDay` clamped to the month's length. */
export function forecastDueDate(month: string, dueDay: number): string {
  assertMonth(month, 'month');
  const [y, m] = month.split('-').map(Number);
  const daysInMonth = new Date(Date.UTC(y, m, 0)).getUTCDate();
  return `${month}-${String(Math.min(dueDay, daysInMonth)).padStart(2, '0')}`;
}

/**
 * NET-DOWN: the forecast row for a month is the method's figure MINUS what
 * reality (every non-forecast source) already booked there — never below
 * zero. One euro exists once: when the real invoice lands, the forecast
 * shrinks by exactly that euro instead of standing beside it.
 */
export function netDownGrossCents(targetCents: number, realizedCents: number): number {
  assertIntCents(targetCents, 'targetCents');
  assertIntCents(realizedCents, 'realizedCents');
  return Math.max(0, targetCents - realizedCents);
}
